import DashboardGrid from "@/components/DashboardGrid";
import SectionHeader from "@/components/SectionHeader";
import WeatherChart from "@/components/WeatherChart";
import { useCurrent, useHistory } from "@/hooks/useWeather";
import { formatTime, windDirection, CONDITION_LABEL, CONDITION_ICON } from "@/lib/weatherUtils";
import { motion } from "framer-motion";

export default function Dashboard() {
  const { data, isLoading } = useCurrent();
  const { data: history } = useHistory("24h");
  const Icon = CONDITION_ICON[data?.condition || "clear"];

  return (
    <div data-testid="page-dashboard" className="pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <SectionHeader
            eyebrow="Dashboard live"
            title="Palestrina, Via Loreto"
            subtitle="Tutti i parametri della stazione in un'unica schermata. I dati si aggiornano automaticamente."
          />
          {data && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              data-testid="status-panel"
            >
              <span className="h-10 w-10 rounded-full bg-[#0B132B] text-[#FBBF24] flex items-center justify-center">
                <Icon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-slate-500">Condizione attuale</p>
                <p className="font-medium text-[#0B132B]">{CONDITION_LABEL[data.condition]}</p>
              </div>
              <div className="pl-4 ml-2 border-l border-slate-200">
                <p className="text-xs uppercase tracking-[0.22em] text-slate-500">Aggiornato</p>
                <p className="font-medium text-[#0B132B]">{formatTime(data.ts)}</p>
              </div>
            </motion.div>
          )}
        </div>

        <DashboardGrid data={data} />

        {history?.points?.length > 0 && (
          <div className="mt-16 grid gap-6 lg:grid-cols-2">
            <div className="metric-card p-6 md:p-8">
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Temperatura · 24h</p>
              <h3 className="font-display text-2xl text-[#0B132B] mt-1 mb-4">Andamento termico</h3>
              <WeatherChart points={history.points} dataKey="temperature" unit="°C" color="#EF4444" area testId="chart-temp-24h" />
            </div>
            <div className="metric-card p-6 md:p-8">
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Umidità · 24h</p>
              <h3 className="font-display text-2xl text-[#0B132B] mt-1 mb-4">Umidità relativa</h3>
              <WeatherChart points={history.points} dataKey="humidity" unit="%" color="#0EA5E9" area testId="chart-hum-24h" />
            </div>
            <div className="metric-card p-6 md:p-8">
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Pressione · 24h</p>
              <h3 className="font-display text-2xl text-[#0B132B] mt-1 mb-4">Barometro</h3>
              <WeatherChart points={history.points} dataKey="pressure" unit="hPa" color="#8B5CF6" testId="chart-press-24h" />
            </div>
            <div className="metric-card p-6 md:p-8">
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Vento · 24h</p>
              <h3 className="font-display text-2xl text-[#0B132B] mt-1 mb-4">Velocità del vento</h3>
              <WeatherChart points={history.points} dataKey="wind_speed" unit="km/h" color="#22C55E" testId="chart-wind-24h" />
            </div>
          </div>
        )}

        {isLoading && (
          <div className="mt-16 text-center text-slate-500" data-testid="loading">Caricamento dati…</div>
        )}
      </div>
    </div>
  );
}
